import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { GROUPS, summarizeChanges } from '../../services/git/changeItems';
import { versionFor } from '../../services/git/gitHistory';
import { pairWrites, pairFile } from '../../services/git/renamePairs';
import GitChangeRow from './GitChangeRow';
import GitPutBackButton from './GitPutBackButton';
import { gitWords } from '../../services/git/gitSyncStatus';
import { useGitSyncStore } from '../../store/gitSyncStore';
import GitNothingEdited from './GitNothingEdited';

/**
 * Changes since the last sync for one workspace (git sync Stage 1, card 7
 * set 7b), grouped Requests / Folders / Collections / Environments /
 * Variables / Other, each item with its changed fields and — set 7c —
 * "Discard this change" (back to the last sync). A scope's repository
 * (boards & documents) passes its own loader and `canPutBack` (scopeChanges.js).
 */
// `onSync` is no longer used here: the detail card's head has Sync now (Git page redesign, 05.10.2026).
export default function GitChanges({ workspace, onLoadChanges, onPutBack, syncing, canPutBack = () => true }) {
  const [state, setState] = useState({ loading: true, items: [], files: [], error: null });
  const words = gitWords(workspace);
  // The status the pill reads (scopes are stored under their key, which is `workspace.id` here).
  const status = useGitSyncStore((s) => s.byWorkspace?.[String(workspace.id)]?.status);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { items, files } = await onLoadChanges(workspace);
      setState({ loading: false, items, files, error: null });
    } catch (e) {
      setState({ loading: false, items: [], files: [], error: e?.message || String(e) });
    }
  }, [workspace, onLoadChanges]);

  // Reload when the workspace changes and after a sync finishes.
  useEffect(() => {
    if (!syncing) load();
  }, [load, syncing]);

  const filesByPath = new Map(state.files.map((f) => [f.path, f]));
  const groups = GROUPS.map((g) => [g, state.items.filter((it) => it.group === g)]).filter(([, list]) => list.length);

  const discard = async (item) => {
    // A rename puts every file it covers back in one step (renamePairs.js).
    const writes = pairWrites(item, filesByPath, 'discard', versionFor);
    await onPutBack(workspace, item.paths ? writes : item.path, writes[0].content, 'discard');
    await load();
  };

  return (
    <div className="git-changes" data-testid="git-changes" data-name={workspace.name}>
      <div className="git-changes-top">
        <span className="git-changes-summary">
          {state.loading ? (
            <span className="git-loading">
              <span className="dbm-spinner" aria-hidden /> Looking for changes…
            </span>
          ) : state.items.length ? (
            `${summarizeChanges(state.items)} since ${words.last}`
          ) : null}
        </span>
        <button type="button" className="git-btn git-btn--ghost git-btn--small" onClick={load} disabled={state.loading} aria-label="Look for changes again">
          <RefreshCw size={12} />
          Refresh
        </button>
      </div>
      {state.error && <p className="git-row-error git-changes-error">{state.error}</p>}
      {!state.loading && !state.error && state.items.length === 0 && <GitNothingEdited status={status} words={words} />}
      {groups.map(([group, list]) => (
        <div key={group} className="git-change-group">
          <h3 className="git-label">{group}</h3>
          {list.map((item) => (
            <GitChangeRow
              key={item.id}
              item={item}
              file={pairFile(item, filesByPath)}
              actions={onPutBack && canPutBack(item, 'discard') && <GitPutBackButton item={item} mode="discard" words={words} disabled={syncing} onConfirm={() => discard(item)} />}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
